import { CheckSquareOutlined, FontColorsOutlined, FormOutlined, LoadingOutlined } from "@ant-design/icons";
import { Button, Form, Input, message, Radio, Flex, Tag, Switch, Tooltip } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { RecordApi, type RecordDTO } from "../apis/record";
import dayjs from "dayjs";
import { AIApi } from "../apis/ai";
import { coinEventEmitter, COIN_CHANGED_EVENT } from "../utils/coinEvent";
import { appendCoinReceipt } from "../utils/coinReceipt";
import { renderTextWithLinks } from "../utils/utils";
import React from "react";
import DurationTimer, { type DurationTimerHandle } from "./DurationTimer";
import AnswerModal from "./AnswerModal";

const { TextArea } = Input;
interface AnswerProps {
    placeholder: string,
    topicId: number,
    closeModal: () => void,
    title: string,
    lastStatus?: boolean,
    fresh?: () => void
}

type CompareLayout = 'vertical' | 'horizontal';
const ANSWER_COMPARE_LAYOUT_KEY = 'jtable_answer_compare_layout';

function safeReadLayout(): CompareLayout {
    try {
        const v = localStorage.getItem(ANSWER_COMPARE_LAYOUT_KEY);
        return v === 'horizontal' ? 'horizontal' : 'vertical';
    } catch {
        return 'vertical';
    }
}

function safeWriteLayout(layout: CompareLayout) {
    try {
        localStorage.setItem(ANSWER_COMPARE_LAYOUT_KEY, layout);
    } catch {
        // ignore
    }
}

function shouldCollapseText(text: unknown, opts?: { maxChars?: number; maxLines?: number }) {
    const str = typeof text === 'string' ? text : '';
    const maxChars = opts?.maxChars ?? 220;
    const maxLines = opts?.maxLines ?? 6;
    const lineCount = str ? str.split(/\r?\n/).length : 0;
    return str.length > maxChars || lineCount > maxLines;
}

function CollapsibleBlock({
    value,
    placeholder,
    render,
    maxPreviewLines = 6,
    backgroundColor = '#f6ffed',
}: {
    value?: unknown;
    placeholder?: React.ReactNode;
    render?: (text: string) => React.ReactNode;
    maxPreviewLines?: number;
    backgroundColor?: string;
}) {
    const text = typeof value === 'string' ? value : '';
    const [expanded, setExpanded] = useState(false);
    const needCollapse = useMemo(() => shouldCollapseText(text, { maxLines: maxPreviewLines }), [text, maxPreviewLines]);

    return (
        <div>
            <div
                style={{
                    padding: '4px 11px',
                    minHeight: '32px',
                    border: '1px solid #d9d9d9',
                    borderRadius: '6px',
                    backgroundColor,
                    wordBreak: 'break-word',
                    whiteSpace: 'pre-wrap',
                    overflow: 'hidden',
                    maxHeight: !needCollapse || expanded ? undefined : `${maxPreviewLines * 1.6}em`,
                }}
            >
                {text
                    ? (render ? render(text) : text)
                    : (placeholder ?? <span style={{ color: '#bfbfbf' }}>暂无内容</span>)}
            </div>
            {needCollapse && (
                <div style={{ marginTop: 6, textAlign: 'right' }}>
                    <Button type="link" size="small" onClick={() => setExpanded(v => !v)} style={{ padding: 0, height: 'auto' }}>
                        {expanded ? '收起' : '展开'}
                    </Button>
                </div>
            )}
        </div>
    );
}

const AI_MODEL_FLASH = 'deepseek-v4-flash';
const AI_MODEL_PRO = 'deepseek-v4-pro';

export default function Answer({ placeholder, topicId, closeModal, title, lastStatus, fresh }: AnswerProps) {
    const [form] = Form.useForm();
    const [record, setRecord] = useState<RecordDTO>();
    const [historyRecords, setHistoryRecords] = useState([]);
    const [showRightAnswer, setShowRightAnswer] = useState(false);
    const [showAILoading, setShowAILoading] = useState(true);
    const [compareLayout, setCompareLayout] = useState<CompareLayout>(() => safeReadLayout());
    const [useAiPro, setUseAiPro] = useState(false);
    const durationTimerRef = useRef<DurationTimerHandle>(null);
    const colors = ["magenta", "red", "volcano", "orange", "gold", "lime", "green", "cyan", "blue", "purple"];
    // 检验、提交
    const handleCheck = (needAI: boolean) => {
        // 计时中点保存必须立刻停表累加。timeout 里读到的表单仍是上一次已确认的分钟，
        // 当前这一段还没 onChange，会把最后一次计时漏掉。
        const flushedMinutes = durationTimerRef.current?.flush();
        if (flushedMinutes !== undefined) {
            form.setFieldsValue({ durationSec: flushedMinutes });
        }
        setTimeout(() => {
            const newData = form.getFieldsValue();
            if (flushedMinutes !== undefined) {
                newData.durationSec = flushedMinutes;
            }
            const data = {
                ...record,
                ...newData,
                submitTime: dayjs().format('YYYY-MM-DD'),
                topicTitle: title,
                lastStatus,
                isCheck: needAI,
            }
            // 二次校验：仅拉 AI，同时仍把当前草稿（含时长）存档
            const onlyAiAfterReveal = needAI && showRightAnswer;
            // 提交时必须填时长 + 是否正确；校验不强制
            if (!needAI && (data?.isCorrect === undefined || data?.durationSec === undefined || data?.durationSec === '')) {
                message.error('请填写必填项');
                return;
            }

            // 判断是否是真实做完题（有 durationSec 和 isCorrect）
            const isRealSubmit = !needAI && data?.isCorrect !== undefined && data?.durationSec !== undefined && data?.durationSec !== '';

            RecordApi.update(data).then(res => {
                appendCoinReceipt(res?.coinReceipt);
                // 合并提示信息
                if (res?.coinAdded) {
                    const coins = res?.coinsAdded ?? 1;
                    message.success(needAI ? `查询成功，金币 +${coins} 👏🏻` : `提交成功，金币 +${coins} 👏🏻`);
                    // 触发金币变更事件
                    coinEventEmitter.emit(COIN_CHANGED_EVENT);
                } else {
                    message.success(needAI ? '查询成功' : '提交成功');
                }
                setShowRightAnswer(true);
                setShowAILoading(false);
                // 只有在真实做完题时才刷新 api/ltn 更新界面数据
                if (isRealSubmit) {
                    fresh?.();
                }
                if (onlyAiAfterReveal) {
                    handleAISuggest(data?.topicTitle, data?.recentAnswer, data?.rightAnswer);
                }
                // 保存完成后再返回，避免过滤弹窗等列表读到旧数据
                if (!needAI) {
                    closeModal();
                }
            }).catch(e => {
                if (e instanceof Error) {
                    message.error(e.message);
                }
            });
        }, 250)
    };

    // AI 查询建议
    const handleAISuggest = (title: string,recent: string, right: string) => {
        setShowAILoading(true)
        AIApi.compare({ recent, right, title, pro: useAiPro }).then(({suggestion}) => {
            setShowAILoading(false)
            form.setFieldsValue({ AI_suggest: suggestion.join('\n') });
        })
    }

    // 初始化
    useEffect(() => {
        let cancelled = false;
        form.resetFields();
        setShowRightAnswer(false);
        setShowAILoading(true);
        setHistoryRecords([]);
        setRecord(undefined);
        RecordApi.list(topicId).then((res) => {
            if (cancelled) return;
            // 如果已经有 recentAnswer，应该显示正确答案区域
            const hasRecentAnswer = res?.record?.recentAnswer && res.record.recentAnswer.trim() !== '';
            const shouldShowRightAnswer = res?.showRightAnswer || hasRecentAnswer;
            setShowRightAnswer(shouldShowRightAnswer);
            setShowAILoading(!shouldShowRightAnswer)
            setHistoryRecords(res?.historyRecords || []);
            form.setFieldsValue(res?.record); // 动态填充表单
            if (res) {
                setRecord({ ...res.record, topicId });
            }
        })
        return () => {
            cancelled = true;
        };
    }, [topicId, form])

    const rightAnswerLabel = (
        <AnswerModal
            key={topicId}
            title={title}
            type="rightAnswer"
            topicId={topicId}
            onSaved={(rightAnswer) => form.setFieldsValue({ rightAnswer })}
        >
            <Tooltip title="修改题目答案">
                <span>
                    正确答案
                    <FormOutlined style={{ marginLeft: 6 }} />
                </span>
            </Tooltip>
        </AnswerModal>
    );

    return <Form form={form}>
        {showRightAnswer && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                <Radio.Group
                    size="small"
                    value={compareLayout}
                    optionType="button"
                    buttonStyle="solid"
                    onChange={(e) => {
                        const next = (e.target?.value ?? 'vertical') as CompareLayout;
                        setCompareLayout(next);
                        safeWriteLayout(next);
                    }}
                    options={[
                        { label: '上下', value: 'vertical' },
                        { label: '左右', value: 'horizontal' },
                    ]}
                />
            </div>
        )}

        {/* 填写答案 + 正确答案（可切换上下/左右） */}
        {showRightAnswer && compareLayout === 'horizontal' ? (
            <Flex gap={12} align="start" style={{ width: '100%' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <Form.Item name="recentAnswer" label="填写答案">
                        <TextArea
                            key="answer"
                            placeholder={placeholder}
                            style={{
                                resize: 'both',
                            }}
                            autoSize={{ minRows: 1 }}
                        />
                    </Form.Item>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <Form.Item name="rightAnswer" label={rightAnswerLabel}>
                        <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues?.rightAnswer !== currentValues?.rightAnswer}>
                            {({ getFieldValue }) => {
                                const rightAnswer = getFieldValue('rightAnswer');
                                return (
                                    <CollapsibleBlock
                                        value={rightAnswer}
                                        placeholder={<span style={{ color: '#bfbfbf' }}>{placeholder}</span>}
                                        render={(t) => renderTextWithLinks(t)}
                                        maxPreviewLines={6}
                                    />
                                );
                            }}
                        </Form.Item>
                    </Form.Item>
                </div>
            </Flex>
        ) : (
            <>
                <Form.Item name="recentAnswer" label="填写答案">
                    <TextArea
                        key="answer"
                        placeholder={placeholder}
                        style={{
                            resize: 'both',
                        }}
                        autoSize={{ minRows: 1 }}
                    />
                </Form.Item>
                {showRightAnswer && (
                    <Form.Item name="rightAnswer" label={rightAnswerLabel}>
                        <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues?.rightAnswer !== currentValues?.rightAnswer}>
                            {({ getFieldValue }) => {
                                const rightAnswer = getFieldValue('rightAnswer');
                                return (
                                    <CollapsibleBlock
                                        value={rightAnswer}
                                        placeholder={<span style={{ color: '#bfbfbf' }}>{placeholder}</span>}
                                        render={(t) => renderTextWithLinks(t)}
                                        maxPreviewLines={6}
                                    />
                                );
                            }}
                        </Form.Item>
                    </Form.Item>
                )}
            </>
        )}

        {showRightAnswer && (<>
            <Form.Item name="AI_suggest" label="AI 引导">
                {(showRightAnswer && showAILoading) 
                ?  <LoadingOutlined /> 
                : (
                    <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues?.AI_suggest !== currentValues?.AI_suggest}>
                        {({ getFieldValue }) => (
                            <CollapsibleBlock
                                value={getFieldValue('AI_suggest')}
                                placeholder={<span style={{ color: '#bfbfbf' }}>点击下方「校验」获取 AI 学习引导</span>}
                                maxPreviewLines={8}
                                backgroundColor="#fbfff8"
                            />
                        )}
                    </Form.Item>
                )}
            </Form.Item>
            {historyRecords?.length > 0 && <Form.Item name="historyRecords" label="历史做题记录">
                <Flex gap="4px 0" wrap>
                    {historyRecords.map((item, index) => <Tag color={colors[index % 10]}>{item}</Tag>)}
                </Flex>
            </Form.Item>}
            <Form.Item name="wrongNotes" label="历史错误信息">
                <TextArea
                    style={{
                        resize: 'both',
                    }}
                    autoSize={{ minRows: 1, maxRows: 12 }}
                ></TextArea>
            </Form.Item>
        </>)}
        {/* 做题时长：初次只显示开始计时；校验后 / 已有累积值才显示输入框 */}
        <Form.Item
            name="durationSec"
            label="做题时长"
            rules={showRightAnswer ? [{ required: true, message: '请填写做题时长' }] : undefined}
        >
            <DurationTimer
                key={topicId}
                timerRef={durationTimerRef}
                forceShowInput={showRightAnswer}
            />
        </Form.Item>
        {showRightAnswer && (
            <Form.Item name="isCorrect" label="是否正确" rules={[{ required: true }]}>
                <Radio.Group
                    options={[
                        { value: true, label: '正确' },
                        { value: false, label: '需重做' },
                    ]}
                />
            </Form.Item>
        )}
        {/* 按钮 */}
        <Form.Item label={null} className='check-btn-wrap'>
            <Flex align="center" justify="flex-end" gap={8} style={{ width: '100%' }}>
                <Tooltip title={useAiPro ? AI_MODEL_PRO : AI_MODEL_FLASH}>
                    <Switch
                        size="small"
                        checked={useAiPro}
                        checkedChildren="Pro"
                        unCheckedChildren="Flash"
                        onChange={setUseAiPro}
                    />
                </Tooltip>
                <Button onClick={() => handleCheck(true)} icon={<FontColorsOutlined />}>
                    校验
                </Button>
                {showRightAnswer && (
                    <Button type="primary" onClick={() => handleCheck(false)} icon={<CheckSquareOutlined />}>
                        提交
                        <span className="coin-action-mark" aria-hidden="true" />
                    </Button>
                )}
            </Flex>
        </Form.Item>
    </Form>
}